import { useEffect, useMemo, useState } from "react";
import {
    db,
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    where,
    updateDoc,
    doc,
} from "../firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ Ajustá este import para que apunte al mismo helper que usás en Dashboard
import { sendInfobipTemplate } from "../utils/sendInfobipTemplate";

export default function OrdersScreen() {
    /* ---------- state ---------- */
    const [orders, setOrders] = useState([]);
    const [initialOrders, setInitialOrders] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);

    /* modal pedido */
    const [showModal, setShowModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    /* catálogo (precios + fotos) */
    const [catalogBySku, setCatalogBySku] = useState({}); // { [sku]: docData }
    const [catalogLoading, setCatalogLoading] = useState(false);

    /* foto producto */
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [photoData, setPhotoData] = useState(null); // { sku, name, imageUrl }

    /* template */
    const [sendingTemplate, setSendingTemplate] = useState(false);

    /* ---------- initial load ---------- */
    useEffect(() => {
        fetchInitialOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* 1. primer lote -------------------------------------------------------- */
    const fetchInitialOrders = async () => {
        try {
            setLoading(true);
            const q = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(30));
            const snapshot = await (await import("firebase/firestore")).getDocs(q);

            if (!snapshot.empty) {
                const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                setOrders(docs);
                setInitialOrders(docs);
                setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
                if (snapshot.size < 30) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error al cargar pedidos");
        } finally {
            setLoading(false);
        }
    };

    /* 2. paginación --------------------------------------------------------- */
    const fetchMoreOrders = async () => {
        if (!lastDoc) return;
        try {
            setLoading(true);
            const q = query(
                collection(db, "orders"),
                orderBy("createdAt", "desc"),
                startAfter(lastDoc),
                limit(30)
            );
            const snap = await (await import("firebase/firestore")).getDocs(q);

            if (!snap.empty) {
                const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setOrders((prev) => [...prev, ...docs]);
                setInitialOrders((prev) => [...prev, ...docs]);
                setLastDoc(snap.docs[snap.docs.length - 1]);
                if (snap.size < 30) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error al cargar más pedidos");
        } finally {
            setLoading(false);
        }
    };

    /* ---------- helpers ---------- */
    const formatDate = (ms) =>
        new Date(ms).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });

    const formatMoney = (n) =>
        new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(Number(n || 0));

    const normalizePhoneTo549 = (raw = "") => {
        const digits = String(raw).replace(/\D/g, "");
        if (!digits) return "";
        if (digits.startsWith("549")) return digits;
        if (digits.startsWith("54")) return "549" + digits.slice(2);
        return "549" + digits.replace(/^0+/, "");
    };

    function chunkArray(arr, size) {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
    }

    /** Normalización tolerante para comparar NOMBRES en memoria */
    const normName = (s = "") =>
        String(s || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toUpperCase()
            .replace(/\uFFFD/g, "")
            .replace(/[’'".,;:()\-_/\\]/g, " ")
            .replace(/[^A-Z0-9 ]+/g, " ")
            .replace(/\s+/g, " ")
            .trim();

    /** Query-safe: variantes del nombre REAL para Firestore (sin normalizar a mayúsculas) */
    function nameQueryVariants(raw = "") {
        const a = String(raw || "").trim();
        const b = a.replace(/\s+/g, " "); // colapsa espacios
        const variants = [a, b].filter(Boolean);
        return [...new Set(variants)];
    }

    function getStatusBadge(statusRaw) {
        const s = String(statusRaw || "").toLowerCase();
        if (s === "pending" || s === "pendiente") return { label: "Pendiente", cls: "badge-warning" };
        if (s === "aceptado" || s === "accepted") return { label: "Aceptado", cls: "badge-success" };
        if (s === "rechazado" || s === "rejected") return { label: "Rechazado", cls: "badge-error" };
        return { label: statusRaw || "--", cls: "badge-ghost" };
    }

    /**
     * ✅ Items del pedido: preferimos rawWebhook.items (trae name/quantity/unit_price/total_price)
     */
    function getProductsArray(order) {
        const rawItems = order?.rawWebhook?.items;
        if (Array.isArray(rawItems) && rawItems.length) {
            return rawItems.map((it) => ({
                sku: it?.sku ? String(it.sku).trim() : "",
                name: it?.name ?? "",
                quantity: Number(it?.quantity ?? 0),
                unit_price: Number(it?.unit_price ?? 0),
                total_price: Number(it?.total_price ?? 0),
            }));
        }

        // fallback viejo
        const products = Array.isArray(order?.products) ? order.products : [];
        return products.map((p) => ({
            sku: String(p?.product_id || "").trim(),
            name: "",
            quantity: Number(p?.quantity ?? 0),
            unit_price: 0,
            total_price: 0,
        }));
    }

    function getOrderCountsFromItems(items) {
        const distinctProducts = items.length;
        const totalUnits = items.reduce((acc, it) => acc + Number(it?.quantity ?? 0), 0);
        return { distinctProducts, totalUnits };
    }

    async function fetchCatalogForSkus(skus) {
        const clean = [...new Set((skus || []).map((s) => String(s || "").trim()).filter(Boolean))];
        if (!clean.length) return {};

        const chunks = chunkArray(clean, 10); // "in" max 10
        const { getDocs } = await import("firebase/firestore");

        const out = {};
        for (const skuChunk of chunks) {
            const q = query(collection(db, "productsCatalog"), where("sku", "in", skuChunk));
            const snap = await getDocs(q);
            snap.forEach((d) => {
                const data = d.data();
                const sku = String(data?.sku || "").trim();
                if (sku) out[sku] = data;
            });
        }
        return out;
    }

    /**
     * ✅ FIX: traer catálogo por "name" REAL (no normalizado),
     * y probando variantes (trim y colapsar espacios).
     */
    async function fetchCatalogForNamesExactWithVariants(rawNames) {
        const names = (rawNames || [])
            .flatMap((n) => nameQueryVariants(n))
            .filter(Boolean);

        const clean = [...new Set(names)];
        if (!clean.length) return {};

        const chunks = chunkArray(clean, 10); // "in" max 10
        const { getDocs } = await import("firebase/firestore");

        const out = {};
        for (const nameChunk of chunks) {
            const q = query(collection(db, "productsCatalog"), where("name", "in", nameChunk));
            const snap = await getDocs(q);
            snap.forEach((d) => {
                const data = d.data();
                const sku = String(data?.sku || "").trim();
                if (sku) out[sku] = data;
            });
        }
        return out;
    }

    function buildCatalogNameIndex(catalogMap) {
        const idx = {}; // { [normName]: [{ sku, data }] }
        for (const [sku, data] of Object.entries(catalogMap || {})) {
            const key = normName(data?.name || "");
            if (!key) continue;
            if (!idx[key]) idx[key] = [];
            idx[key].push({ sku, data });
        }
        return idx;
    }

    function rescueSkuByName(itemName, nameIndex) {
        const key = normName(itemName || "");
        if (!key) return { sku: "", data: null };
        const matches = nameIndex?.[key] || [];
        if (!matches.length) return { sku: "", data: null };
        return { sku: matches[0].sku, data: matches[0].data };
    }

    function computeOrderTotals(order, catalogMap) {
        const items = getProductsArray(order);
        const nameIndex = buildCatalogNameIndex(catalogMap);

        let totalUnits = 0;
        let totalOrder = 0;

        const lines = items.map((it) => {
            let sku = String(it?.sku || "").trim();

            // ✅ rescate por nombre contra catálogo (ya cargado por sku + por name)
            let rescuedData = null;
            if (!sku) {
                const res = rescueSkuByName(it?.name, nameIndex);
                sku = res.sku || "";
                rescuedData = res.data || null;
            }

            const cat = sku ? catalogMap?.[sku] : null;
            const data = cat || rescuedData;

            const qty = Number(it?.quantity ?? 0);
            const fromRawUnit = Number(it?.unit_price ?? 0);
            const fromRawTotal = Number(it?.total_price ?? 0);
            const unitFromCatalog = Number(data?.price ?? 0);

            const unitPrice = fromRawUnit > 0 ? fromRawUnit : unitFromCatalog;
            const name = it?.name || data?.name || sku || "--";
            const imageUrl = data?.imageUrl || null;

            const lineTotal = fromRawTotal > 0 ? fromRawTotal : unitPrice * qty;

            totalUnits += qty;
            totalOrder += lineTotal;

            return {
                sku: sku || "--",
                name,
                qty,
                unitPrice,
                lineTotal,
                imageUrl,
                matched: !!data,
            };
        });

        return { lines, totalUnits, distinctProducts: items.length, totalOrder };
    }

    /* 3. filtrado en vivo --------------------------------------------------- */
    const filteredOrders = orders.filter((o) => {
        const email = (o?.customer?.email || o?.email || "").toLowerCase();
        const cell = String(
            o?.cellPhone || o?.rawWebhook?.customer?.billing_info?.buyerPhone || o?.phone || ""
        ).toLowerCase();
        const orderNum = String(o?.orderNumber || "").toLowerCase();
        const status = (o?.status || "").toLowerCase();
        const search = searchTerm.toLowerCase();
        return email.includes(search) || orderNum.includes(search) || status.includes(search) || cell.includes(search);
    });

    /* 4. búsqueda directa a Firestore -------------------------------------- */
    const handleDatabaseSearch = async () => {
        if (!searchTerm.trim()) {
            toast.info("Ingresa un término de búsqueda");
            return;
        }
        try {
            setLoading(true);

            const maybeNumber = Number(searchTerm);
            const q = Number.isFinite(maybeNumber)
                ? query(collection(db, "orders"), where("orderNumber", "==", maybeNumber), limit(30))
                : query(collection(db, "orders"), where("customer.email", "==", searchTerm.trim()), limit(30));

            const snap = await (await import("firebase/firestore")).getDocs(q);

            if (!snap.empty) {
                const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setOrders(docs);
                setHasMore(false);
            } else {
                setOrders([]);
                setHasMore(false);
                toast.info("Sin resultados para la búsqueda");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error en la búsqueda");
        } finally {
            setLoading(false);
        }
    };

    /* 5. limpiar búsqueda --------------------------------------------------- */
    const handleClearSearch = () => {
        setOrders(initialOrders);
        setHasMore(true);
        setSearchTerm("");
    };

    /* =========================================================
       CSV compatible Excel Argentina (es-AR)
       ========================================================= */

    const CSV_SEP = ";";

    function escapeCsvValue(v) {
        const s = String(v ?? "");
        if (s.includes('"') || s.includes(CSV_SEP) || /[\r\n]/.test(s)) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    }

    function csvRow(arr) {
        return arr.map(escapeCsvValue).join(CSV_SEP);
    }

    function downloadCsvExcel(filename, csv) {
        const BOM = "\uFEFF"; // 🔑 UTF-8 correcto para Excel Windows
        const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        URL.revokeObjectURL(url);
    }


    function orderToCSV(order, modalTotals) {
        const rows = [];

        // Header
        rows.push(
            csvRow([
                "SKU",
                "Producto",
                "Precio unitario",
                "Cantidad",
                "Total producto",
                "Tiene match catálogo",
            ])
        );

        // Líneas
        for (const l of modalTotals.lines) {
            rows.push(
                csvRow([
                    l.sku || "",
                    l.name,
                    Number(l.unitPrice || 0),   // número sin $
                    Number(l.qty || 0),
                    Number(l.lineTotal || 0),
                    l.matched ? "SI" : "NO",
                ])
            );
        }

        // Totales
        rows.push("");
        rows.push(csvRow(["Productos distintos", modalTotals.distinctProducts]));
        rows.push(csvRow(["Total unidades", modalTotals.totalUnits]));
        rows.push(csvRow(["Total pedido", modalTotals.totalOrder]));

        return rows.join("\r\n"); // 🔑 salto Excel
    }


    const handleDownloadOrderExcel = () => {
        if (!selectedOrder || !modalTotals) return;
        const csv = orderToCSV(selectedOrder, modalTotals);
        downloadCsvExcel(
            `pedido_${selectedOrder.orderNumber}.csv`,
            csv
        );
    };


    /* ---------- enviar template ---------- */
    const handleSendTemplateToClient = async () => {
        if (!selectedOrder) return;

        const rawPhone =
            selectedOrder?.cellPhone ||
            selectedOrder?.rawWebhook?.customer?.billing_info?.buyerPhone ||
            selectedOrder?.customer?.buyerPhone ||
            selectedOrder?.phone ||
            "";

        const to = normalizePhoneTo549(rawPhone);
        if (!to) {
            toast.error("El pedido no tiene cellPhone válido");
            return;
        }

        try {
            setSendingTemplate(true);
            await sendInfobipTemplate({
                to,
                templateName: "order_followup", // <-- CAMBIAR por tu template real
                variables: { orderNumber: selectedOrder.orderNumber ?? "" },
                companyId: selectedOrder.companyId || "phiphitoys",
            });
            toast.success("Template enviado");
        } catch (e) {
            console.error(e);
            toast.error("No se pudo enviar el template");
        } finally {
            setSendingTemplate(false);
        }
    };

    /* ----- abrir / cerrar modal (loader + catálogo) ----- */
    const openModal = async (order) => {
        setSelectedOrder(order);
        setShowModal(true);
        setCatalogBySku({});
        setPhotoData(null);
        setShowPhotoModal(false);

        const items = getProductsArray(order);

        // 1) catálogo por SKUs existentes
        const skus = items.map((it) => it.sku).filter(Boolean);

        // 2) catálogo por NAME real (con variantes) para items sin SKU
        const missingSkuNames = items
            .filter((it) => !String(it?.sku || "").trim())
            .map((it) => it?.name)
            .filter(Boolean);

        try {
            setCatalogLoading(true);

            const [bySku, byName] = await Promise.all([
                fetchCatalogForSkus(skus),
                fetchCatalogForNamesExactWithVariants(missingSkuNames),
            ]);

            const merged = { ...bySku, ...byName };
            setCatalogBySku(merged);
        } catch (e) {
            console.error(e);
            toast.error("No se pudo cargar el catálogo de productos");
            setCatalogBySku({});
        } finally {
            setCatalogLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedOrder(null);
        setShowModal(false);
        setCatalogBySku({});
        setCatalogLoading(false);
        setPhotoData(null);
        setShowPhotoModal(false);
    };

    /* ----- actualizar estado -------- */
    const changeStatus = async (newStatus) => {
        if (!selectedOrder) return;
        try {
            await updateDoc(doc(db, "orders", selectedOrder.id), { status: newStatus });
            toast.success(`Estado actualizado a "${newStatus}"`);
            setOrders((prev) => prev.map((o) => (o.id === selectedOrder.id ? { ...o, status: newStatus } : o)));
            closeModal();
        } catch (err) {
            console.error(err);
            toast.error("No se pudo actualizar el estado");
        }
    };

    /* ---------- preview tabla: solo cantidades ---------- */
    const orderPreviewSummary = (order) => {
        const items = getProductsArray(order);
        const { distinctProducts, totalUnits } = getOrderCountsFromItems(items);
        return `${distinctProducts} productos · ${totalUnits} unidades`;
    };

    /* ---------- datos calculados del modal ---------- */
    const modalTotals = useMemo(() => {
        if (!selectedOrder) return { lines: [], totalUnits: 0, distinctProducts: 0, totalOrder: 0 };
        return computeOrderTotals(selectedOrder, catalogBySku);
    }, [selectedOrder, catalogBySku]);

    const openProductPhoto = (line) => {
        if (!line?.imageUrl) {
            toast.info("Este producto no tiene imageUrl en productsCatalog (o no se pudo matchear).");
            return;
        }
        setPhotoData({ sku: line.sku, name: line.name, imageUrl: line.imageUrl });
        setShowPhotoModal(true);
    };

    const closePhotoModal = () => {
        setPhotoData(null);
        setShowPhotoModal(false);
    };

    /* ---------- render: datos cliente ---------- */
    const customerEmail = selectedOrder?.customer?.email || selectedOrder?.email || "";
    const businessName =
        selectedOrder?.customer?.businessName || selectedOrder?.rawWebhook?.customer?.billing_info?.businessName || "";
    const cuit = selectedOrder?.customer?.cuit || selectedOrder?.rawWebhook?.customer?.billing_info?.cuit || "";
    const cellPhone =
        selectedOrder?.cellPhone ||
        selectedOrder?.rawWebhook?.customer?.billing_info?.buyerPhone ||
        selectedOrder?.customer?.buyerPhone ||
        "";

    // ✅ “Nombre” del comprador real (buyerName) y ocultar Xeroo - Development
    const buyerName =
        selectedOrder?.rawWebhook?.customer?.billing_info?.buyerName ||
        selectedOrder?.customer?.fullName ||
        selectedOrder?.rawWebhook?.customer?.full_name ||
        "";

    const shouldShowBuyerName = (() => {
        const s = String(buyerName || "").trim();
        if (!s) return false;
        // no mostrar nombres "de sistema"
        const bad = ["XEROO - DEVELOPMENT", "XEROO DEVELOPMENT", "DEVELOPMENT"];
        if (bad.includes(s.toUpperCase())) return false;
        return true;
    })();

    const statusBadge = getStatusBadge(selectedOrder?.status);









    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Listado de Pedidos</h1>

            {/* Búsqueda */}
            <div className="form-control mb-4">
                <label className="label">
                    <span className="label-text font-bold">Buscar Pedido</span>
                </label>
                <div className="flex items-center space-x-2">
                    <input
                        type="text"
                        placeholder="Ingresa email, número de pedido, estado o celular..."
                        className="input input-bordered w-full"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button className="btn bg-[#e91c24] border-none text-white" onClick={handleDatabaseSearch} disabled={loading}>
                        {loading ? "Buscando..." : "Buscar"}
                    </button>
                    <button className="btn bg-gray-400 border-none text-white" onClick={handleClearSearch} disabled={loading}>
                        Limpiar
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-base-100 p-4 rounded shadow">
                <h2 className="text-lg font-bold mb-2">Pedidos</h2>

                {filteredOrders.length ? (
                    <table className="table w-full">
                        <thead>
                            <tr>
                                <th># Pedido</th>
                                <th>Fecha</th>
                                <th>Email</th>
                                <th>Celular</th>
                                <th>Resumen</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((o) => {
                                const b = getStatusBadge(o?.status);
                                return (
                                    <tr key={o.id} className="cursor-pointer hover:bg-base-200" onClick={() => openModal(o)}>
                                        <td>{o.orderNumber ?? "--"}</td>
                                        <td>{o.createdAt ? formatDate(o.createdAt) : "--"}</td>
                                        <td>{o?.customer?.email || o.email || "--"}</td>
                                        <td>{o.cellPhone || o?.rawWebhook?.customer?.billing_info?.buyerPhone || "--"}</td>
                                        <td>{orderPreviewSummary(o)}</td>
                                        <td>
                                            <span className={`badge ${b.cls}`}>{b.label}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <p>No hay pedidos para mostrar.</p>
                )}

                {hasMore && initialOrders.length > 0 && (
                    <div className="flex justify-center mt-4">
                        <button className="btn bg-[#e91c24] border-none text-white" onClick={fetchMoreOrders} disabled={loading}>
                            {loading ? "Cargando..." : "Cargar más"}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal pedido */}
            {showModal && selectedOrder && (
                <div className="fixed inset-0 bg-black/50 z-50 p-4 overflow-y-auto">
                    <div className="bg-base-100 w-full max-w-4xl mx-auto rounded shadow-lg overflow-hidden mt-6 relative">
                        {/* Botón X */}
                        <button
                            onClick={closeModal}
                            className="btn btn-sm btn-ghost absolute top-2 right-2"
                            aria-label="Cerrar"
                            title="Cerrar"
                        >
                            ✕
                        </button>

                        {/* Header */}
                        <div className="p-4 sm:p-6 border-b">
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <h3 className="text-xl sm:text-2xl font-bold">Pedido #{selectedOrder.orderNumber}</h3>
                                            <span className={`badge ${statusBadge.cls}`}>{statusBadge.label}</span>
                                        </div>

                                        <div className="mt-2 text-sm space-y-1">
                                            <p><strong>Email:</strong> {customerEmail || "--"}</p>
                                            {shouldShowBuyerName && (
                                                <p><strong>Comprador:</strong> {buyerName}</p>
                                            )}
                                            <p><strong>Razón social:</strong> {businessName || "--"}</p>
                                            <p><strong>CUIT:</strong> {cuit || "--"}</p>
                                            <p><strong>Celular:</strong> {cellPhone || "--"}</p>
                                            <p><strong>Fecha:</strong> {selectedOrder.createdAt ? formatDate(selectedOrder.createdAt) : "--"}</p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <button
                                            onClick={handleDownloadOrderExcel}
                                            className="btn bg-blue-600 text-white border-none"
                                            disabled={catalogLoading}
                                        >
                                            {catalogLoading ? "Cargando..." : "Descargar Excel"}
                                        </button>

                                        {/* <button
                                            onClick={handleSendTemplateToClient}
                                            className="btn bg-green-600 text-white border-none"
                                            disabled={sendingTemplate}
                                        >
                                            {sendingTemplate ? "Enviando..." : "Enviar template"}
                                        </button> */}
                                    </div>
                                </div>

                                {/* Indicadores */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="p-4 rounded border bg-base-200">
                                        <div className="text-xs opacity-70">Productos</div>
                                        <div className="text-2xl font-bold">{modalTotals.distinctProducts}</div>
                                    </div>
                                    <div className="p-4 rounded border bg-base-200">
                                        <div className="text-xs opacity-70">Unidades</div>
                                        <div className="text-2xl font-bold">{modalTotals.totalUnits}</div>
                                    </div>
                                    <div className="p-4 rounded border bg-base-200">
                                        <div className="text-xs opacity-70">Total pedido</div>
                                        <div className="text-2xl font-bold">{formatMoney(modalTotals.totalOrder)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* {catalogLoading && (
                            <div className="px-6 py-3 border-b flex items-center gap-3">
                                <span className="loading loading-spinner loading-sm"></span>
                                <span className="text-sm">Cargando catálogo (rescata SKU por nombre + fotos)...</span>
                            </div>
                        )} */}

                        {/* Body */}
                        {/* Body */}
                        <div className="p-4 sm:p-6 max-h-[55vh] overflow-y-auto">
                            {catalogLoading ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <span className="loading loading-spinner loading-lg"></span>
                                    <span className="text-sm opacity-80">Cargando productos y precios...</span>
                                </div>
                            ) : (
                                <>
                                    <h4 className="font-bold mb-3">Productos</h4>

                                    <div className="overflow-x-auto">
                                        <table className="table w-full text-sm">
                                            <thead>
                                                <tr>
                                                    <th>SKU</th>
                                                    <th>Producto</th>
                                                    <th className="text-right">Unitario</th>
                                                    <th className="text-right">Cant.</th>
                                                    <th className="text-right">Total</th>
                                                    <th>Foto</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {modalTotals.lines.map((l, idx) => (
                                                    <tr key={idx} className="hover:bg-base-200">
                                                        <td>{l.sku || "--"}</td>
                                                        <td>{l.name}</td>
                                                        <td className="text-right">{formatMoney(l.unitPrice)}</td>
                                                        <td className="text-right">{l.qty}</td>
                                                        <td className="text-right">{formatMoney(l.lineTotal)}</td>
                                                        <td>
                                                            {l.imageUrl ? (
                                                                <button
                                                                    className="btn btn-xs bg-slate-700 text-white border-none"
                                                                    onClick={() => openProductPhoto(l)}
                                                                >
                                                                    Ver
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs opacity-60">--</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>

                                            <tfoot>
                                                <tr>
                                                    <td colSpan={4} className="text-right font-bold">Total pedido</td>
                                                    <td className="text-right font-bold">{formatMoney(modalTotals.totalOrder)}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>


                        {/* Footer */}
                        <div className="p-4 sm:p-6 border-t flex gap-2">
                            {["pending", "Pendiente"].includes(selectedOrder.status) && (
                                <>
                                    <button onClick={() => changeStatus("Aceptado")} className="btn bg-green-600 text-white border-none flex-1">
                                        Aceptado
                                    </button>
                                    <button onClick={() => changeStatus("Rechazado")} className="btn bg-red-600 text-white border-none flex-1">
                                        Rechazado
                                    </button>
                                </>
                            )}

                            <button onClick={closeModal} className="btn bg-gray-400 text-white border-none flex-1">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal foto */}
            {showPhotoModal && photoData?.imageUrl && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-[60] p-4">
                    <div className="bg-base-100 w-full max-w-3xl rounded shadow-lg overflow-hidden">
                        <div className="p-4 border-b flex items-center justify-between">
                            <div className="font-bold text-sm">
                                {photoData?.name ?? photoData?.sku} {photoData?.sku ? `— ${photoData.sku}` : ""}
                            </div>
                            <button onClick={closePhotoModal} className="btn btn-sm bg-gray-400 text-white border-none">
                                Cerrar
                            </button>
                        </div>
                        <div className="p-4 flex items-center justify-center bg-black/5">
                            <img
                                src={photoData.imageUrl}
                                alt={photoData?.name ?? "Producto"}
                                className="max-h-[70vh] w-auto rounded"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
